import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Ticket } from 'lucide-react'
import { getTickets, createTicket, updateTicket } from '../../services/staffServices'
import { useStaffAuthStore } from '../../store/staffAuthStore'
import StaffLayout from './components/StaffLayout'
import toast from 'react-hot-toast'
import { StaggerContainer, StaggerItem, ModalOverlay, ButtonLoader } from './components/StaffAnimations'

// Schema: id(text, required), customer_name, phone_number, address,
//   title, description, category, priority,
//   status('OPEN'|'IN_PROGRESS'|'RESOLVED'|'CLOSED'),
//   created_by(users.id text), created_by_name(text)

const STATUS_COLORS = {
    OPEN: 'bg-amber-500/15 text-amber-400',
    IN_PROGRESS: 'bg-blue-500/15 text-blue-400',
    RESOLVED: 'bg-green-500/15 text-green-400',
    CLOSED: 'bg-slate-500/15 text-slate-400',
}

const CATEGORIES = ['GENERAL', 'WARRANTY', 'SERVICE', 'COMPLAINT', 'INQUIRY']

function TicketModal({ ticket, profile, onClose, onSave }) {
    const isEdit = !!ticket?.id
    const [form, setForm] = useState({
        id: ticket?.id || '',
        title: ticket?.title || '',
        description: ticket?.description || '',
        customer_name: ticket?.customer_name || '',
        phone_number: ticket?.phone_number || '',
        address: ticket?.address || '',
        category: ticket?.category || 'GENERAL',
        priority: ticket?.priority || 'MEDIUM',
        status: ticket?.status || 'OPEN',
        resolution_notes: ticket?.resolution_notes || '',
    })
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 rounded-2xl border border-white/8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-slate-900">
                    <h2 className="font-semibold text-white">{isEdit ? 'Edit Ticket' : 'New Ticket'}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
                </div>
                <form onSubmit={e => {
                    e.preventDefault()
                    if (!form.title || !form.customer_name || !form.phone_number) {
                        toast.error('Title, customer name & phone required'); return
                    }
                    if (!isEdit && !form.id) { toast.error('Ticket ID required'); return }
                    onSave({
                        ...form,
                        created_by: profile?.id || '',
                        created_by_name: profile?.name || '',
                    })
                }} className="p-5 space-y-3">
                    {!isEdit && (
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Ticket ID *</label>
                            <input value={form.id} onChange={e => set('id', e.target.value)}
                                placeholder="e.g. TKT-2024-001"
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                    )}
                    <div>
                        <label className="text-xs text-slate-400 block mb-1">Title *</label>
                        <input value={form.title} onChange={e => set('title', e.target.value)}
                            className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                    </div>
                    <div>
                        <label className="text-xs text-slate-400 block mb-1">Description</label>
                        <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3}
                            className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 resize-none" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Customer Name *</label>
                            <input value={form.customer_name} onChange={e => set('customer_name', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Phone *</label>
                            <input value={form.phone_number} onChange={e => set('phone_number', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Category</label>
                            <select value={form.category} onChange={e => set('category', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Priority</label>
                            <select value={form.priority} onChange={e => set('priority', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                <option>LOW</option><option>MEDIUM</option><option>HIGH</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Status</label>
                            <select value={form.status} onChange={e => set('status', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                <option value="OPEN">Open</option>
                                <option value="IN_PROGRESS">In Progress</option>
                                <option value="RESOLVED">Resolved</option>
                                <option value="CLOSED">Closed</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Address</label>
                            <input value={form.address} onChange={e => set('address', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        {(form.status === 'RESOLVED' || form.status === 'CLOSED') && (
                            <div className="col-span-2">
                                <label className="text-xs text-slate-400 block mb-1">Resolution Notes</label>
                                <textarea value={form.resolution_notes} onChange={e => set('resolution_notes', e.target.value)} rows={2}
                                    className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 resize-none" />
                            </div>
                        )}
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 rounded-xl text-sm transition">Cancel</button>
                        <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-2.5 rounded-xl text-sm font-medium transition">{isEdit ? 'Update' : 'Create'}</button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default function Tickets() {
    const qc = useQueryClient()
    const { profile } = useStaffAuthStore()
    const [modal, setModal] = useState(null)

    const { data: tickets = [], isLoading } = useQuery({ queryKey: ['tickets'], queryFn: getTickets })

    const createMut = useMutation({ mutationFn: createTicket, onSuccess: () => { qc.invalidateQueries(['tickets']); setModal(null); toast.success('Ticket created') }, onError: e => toast.error(e.message) })
    const updateMut = useMutation({ mutationFn: ({ id, ...r }) => updateTicket(id, r), onSuccess: () => { qc.invalidateQueries(['tickets']); setModal(null); toast.success('Updated') }, onError: e => toast.error(e.message) })

    return (
        <StaffLayout>
            <div className="space-y-5">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-white">Tickets</h1>
                    <button onClick={() => setModal('new')} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-medium transition">
                        <Plus size={16} /> New Ticket
                    </button>
                </div>

                {/* Status board */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'].map(s => (
                        <div key={s} className="bg-slate-900 rounded-xl border border-white/5 p-4">
                            <p className="text-xs text-slate-400">{s.replace('_', ' ')}</p>
                            <p className="text-2xl font-bold text-white mt-1">{tickets.filter(t => t.status === s).length}</p>
                        </div>
                    ))}
                </div>

                <div className="bg-slate-900 rounded-xl border border-white/5 overflow-hidden">
                    {isLoading ? <div className="p-8 text-center text-slate-400">Loading...</div> :
                        tickets.length === 0 ? <div className="p-12 text-center text-slate-500">No tickets</div> : (
                                    <StaggerContainer className="divide-y divide-white/5">
                                        {tickets.map(t => (
                                            <StaggerItem key={t.id}>
                                            <div onClick={() => setModal(t)} className="px-5 py-4 hover:bg-white/5 transition-all duration-200 cursor-pointer">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-3">
                                                <div className="w-9 h-9 rounded-xl bg-slate-800 flex items-center justify-center mt-0.5 shrink-0">
                                                    <Ticket size={15} className="text-amber-400" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-white">{t.title}</p>
                                                    <p className="text-sm text-slate-400">{t.customer_name} · {t.phone_number}</p>
                                                    {t.description && <p className="text-xs text-slate-500 mt-1 max-w-sm truncate">{t.description}</p>}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className={`text-xs px-2 py-1 rounded-lg font-medium ${STATUS_COLORS[t.status] || ''}`}>{t.status?.replace('_', ' ')}</span>
                                                <span className="text-xs text-slate-600">{t.id}</span>
                                            </div>
                                        </div>
                                    </div>
                                </StaggerItem>
                            ))}
                        </StaggerContainer>
                    )}
                </div>
            </div>
            <ModalOverlay show={!!modal}>
                {modal && (
                    <TicketModal
                        ticket={modal === 'new' ? null : modal}
                        profile={profile}
                        onClose={() => setModal(null)}
                        onSave={data => modal === 'new' ? createMut.mutate(data) : updateMut.mutate({ id: modal.id, ...data })}
                    />
                )}
            </ModalOverlay>
        </StaffLayout>
    )
}
